<template>
	<div ref="el" data-component="_image-set-media" :data-video="isVideo">
		<MediaAsset v-if="isVideo" class="media" :media="image" :muted="muted" />
		<NuxtImg v-else class="media" :src="image.src" :alt="image.alt || ''" />

		<ImageSetMuteButton v-if="isVideo" v-model="muted" class="mute-button" />
	</div>
</template>

<script setup>
import { MediaAsset } from '@/components/core'
import { useInViewport } from '~/composables/core'
import ImageSetMuteButton from './ImageSetMuteButton.vue'

const props = defineProps({
	image: { type: Object, required: true },
})

const el = ref(null)
const muted = ref(true)
const isVideo = computed(() => props.image?.type === 'video')
const { viewport } = useInViewport(el)

const getVideo = () => el.value?.querySelector('video')

const pauseAndMuteVideo = () => {
	const video = getVideo()

	muted.value = true
	if (!video) return

	video.muted = true
	video.pause()
}

const playMutedVideo = async () => {
	const video = getVideo()

	muted.value = true
	if (!video) return

	video.muted = true

	try {
		await video.play()
	} catch {
		// The video will still be muted and ready for the next browser-allowed autoplay attempt.
	}
}

watch(viewport, (value) => {
	if (!isVideo.value || !value) return

	if (value.inViewport) {
		playMutedVideo()
		return
	}

	pauseAndMuteVideo()
})
</script>

<style lang="scss" scoped>
[data-component='_image-set-media'] {
	position: relative;
	display: block;

	.media {
		display: block;
		width: 100%;
		height: auto;
	}

	.mute-button {
		position: absolute;
		right: 36px;
		bottom: 36px;
		z-index: 2;
	}

	@include max-width($xs) {
		.mute-button {
			right: 16px;
			bottom: 16px;
		}
	}
}
</style>
